import React, { useState } from 'react';

import { Button, Flex, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

import {
  CreateAppointmentResponse,
  useCreateAppointmentMutation
} from '@/store/slices/api/schedules/schedulesApiSlice';
import { getTomorrow } from '@/utils/constants';
import formatDate from '@/utils/formatDate';
import DatePicker from '@/components/DatePickers/DatePicker';

import styles from '../DeviceScheduleModal.module.scss';
import SchedulesSummary from '../SchedulesSummary';
import { EditScheduleStepProps } from '../types';

const InClinicSchedule: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  loading,
  onSuccess,
  setLoading,
  patientSFId,
  deviceInfo
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [apiResponse, setApiResponse] = useState<CreateAppointmentResponse | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);

  const [createAppointment] = useCreateAppointmentMutation();

  const handleCreate = async () => {
    if (!scheduledDate || !patientSFId || !deviceInfo) return;

    setLoading(true);

    try {
      const formattedDate = formatDate(scheduledDate, 'YYYY-MM-DD');

      const appointmentData = {
        deviceId: deviceInfo.deviceId,
        patientSFId: patientSFId,
        scheduleDate: formattedDate,
        type: 'In Clinic' as const,
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber
      };

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        notifications.show({
          title: 'Error',
          message: result.message || 'Failed to create In Clinic appointment',
          color: 'red',
          icon: <IconX size="1.1rem" />,
          autoClose: 5000
        });
        return;
      }

      notifications.show({
        title: 'Success',
        message: result.message || 'In Clinic appointment created successfully',
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });

      setApiResponse(result);
      setShowSummary(true);
    } catch (error) {
      console.error('Error creating In Clinic appointment:', error);

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success) {
          notifications.show({
            title: 'Error',
            message: errorData.message || 'Failed to create In Clinic appointment',
            color: 'red',
            icon: <IconX size="1.1rem" />,
            autoClose: 5000
          });
          return;
        }
      }

      notifications.show({
        title: 'Error',
        message: 'Failed to create In Clinic appointment',
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowSummary(false);
    setApiResponse(null);
    setScheduledDate(null);

    if (apiResponse?.success) {
      onSuccess?.();
    }

    onClose();
  };

  if (showSummary && apiResponse?.summary?.nextDates) {
    return (
      <SchedulesSummary
        appointmentType="In Clinic"
        nextDates={apiResponse.summary.nextDates}
        onClose={handleClose}
      />
    );
  }

  return (
    <Stack gap={'xs'}>
      <Flex px={20} pt={16} direction={'column'}>
        <Title order={5} fw={600} mb={4} c={'plum.9'}>
          In Clinic Appointment
        </Title>
        <Text size="xs" c={'plum'} mb={8}>
          Select a schedule date for the In Clinic appointment. If any existing remote appointments
          conflict with account or patient level buffers, the dates will shift to comply.
        </Text>
        <Grid columns={6} mt={8}>
          <Grid.Col span={3}>
            <Text size="xs" c={'plum'} mb={4} fw={500}>
              Scheduled Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: scheduledDate,
                onChange: setScheduledDate,
                isClearable: false,
                minDate: getTomorrow(),
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
          </Grid.Col>
        </Grid>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleCreate}
          className={styles.modalButton}
          disabled={!scheduledDate || loading}
          loading={loading}>
          Create
        </Button>
      </Group>
    </Stack>
  );
};

export default InClinicSchedule;
